import {
  BrandKitCreateOkResponse,
  BrandKitLibraryCreate,
  BrandKitListOkResponse,
  BrandKitUpdateOkResponse,
  brandKitCreate,
  brandKitList,
  brandKitUpdate
} from '@goldcast/api/content';
import { BrandKit, listBrandKit, partialUpdateBrandKit } from '@goldcast/api/backend';
import createStore from './store';
import { currentClip } from './clip';
import { loadVoiceProfileList } from './voiceProfile';
import {
  BrandKitCreateResponse,
  BrandKitStore,
  ClipsBrandKitStore,
  PlatformBrandKitStore
} from '@/player-config/src/types/brand-kit-types';
import { fontsMap } from '@/Pages/Clip/SideBar/FontSelector/constants';
import { getFileNameFromPath, getFileNameWithoutExtension } from '@/player-config/src/utils/fileUtils';
import { getBrandKitUrl, loadBrandKitFonts } from '@/Pages/BrandKit/components/utils';
import { getCurrentClipFontNamePure, getFontNameFromBrandKitPure } from '@/player-config/src/utils/fontUtils';

const EMPTY_BRAND_KIT = {
  background: [],
  intro_outro: [],
  fonts: [],
  colors: []
};

export const brandKit = createStore<BrandKitStore>({
  id: null,
  ...EMPTY_BRAND_KIT
});

export const clipsBrandKit = createStore<ClipsBrandKitStore>({});

export const platformBrandKitStore = createStore<PlatformBrandKitStore>({
  availableBrandKits: [],
  isLoadingBrandKits: false,
  ftuxBrandKit: [
    {
      id: null,
      ...EMPTY_BRAND_KIT
    }
  ]
});

/**
 * Persists a newly uploaded font path to the FTUX brand kit for a given project.
 * Uses the already-loaded store state to avoid a redundant list API call, then
 * updates the store in-place so callers don't need to re-fetch.
 */
export function saveFtuxBrandKitFont(fontPath: string, projectId: string): Promise<void> {
  const { ftuxBrandKit } = platformBrandKitStore.getSnapshot();
  const updatedFonts = [...(ftuxBrandKit?.fonts ?? []), fontPath];

  const savePromise = ftuxBrandKit?.id
    ? brandKitUpdate({
        id: ftuxBrandKit.id,
        body: { ...ftuxBrandKit, fonts: updatedFonts, library_type: 'FTUX' }
      })
    : brandKitCreate({
        body: {
          fonts: updatedFonts,
          library_type: 'FTUX',
          project_id: projectId
        } as BrandKitLibraryCreate
      });

  return savePromise.then(response => {
    platformBrandKitStore.update(draft => {
      draft.ftuxBrandKit = ftuxBrandKit?.id
        ? { ...ftuxBrandKit, fonts: updatedFonts }
        : { ...response, fonts: updatedFonts };
    });
  });
}

/**
 * Loads FTUX brand kit fonts for a given project and updates the store.
 * Should be called after loadPlatformBrandKits when the project ID is known.
 */
export function loadFtuxBrandKitFonts(projectId: string): Promise<void> {
  return brandKitList({
    queryParams: {
      library_type: 'FTUX',
      project_id: projectId,
      limit: 1
    } as Parameters<typeof brandKitList>[0]['queryParams']
  })
    .then(response => {
      const ftuxKit = response?.results?.[0] ?? null;
      platformBrandKitStore.update(draft => {
        draft.ftuxBrandKit = ftuxKit;
      });
    })
    .catch(err => Promise.reject(err));
}

export function loadPlatformBrandKits(): Promise<void> {
  const { isLoadingBrandKits } = platformBrandKitStore.getSnapshot();
  if (isLoadingBrandKits) {
    return Promise.resolve();
  }

  platformBrandKitStore.update(draft => {
    draft.isLoadingBrandKits = true;
    draft.loadError = undefined;
  });

  return listBrandKit({ queryParams: {} })
    .then(response => {
      platformBrandKitStore.update(draft => {
        draft.availableBrandKits = response as BrandKit[];
        draft.isLoadingBrandKits = false;
      });
    })
    .catch(() => {
      platformBrandKitStore.update(draft => {
        draft.isLoadingBrandKits = false;
        draft.loadError = 'Failed to load brand kits';
      });
    });
}

export function selectPlatformBrandKit(brandKitId: string): void {
  const { availableBrandKits, selectedBrandKitId } = platformBrandKitStore.getSnapshot();

  const previousDefaultId =
    selectedBrandKitId ?? availableBrandKits.find(kit => kit.is_default)?.id ?? availableBrandKits[0]?.id;

  platformBrandKitStore.update(draft => {
    draft.selectedBrandKitId = brandKitId;
    draft.availableBrandKits = draft.availableBrandKits.map(kit => ({
      ...kit,
      is_default: kit.id === brandKitId
    }));
  });

  const patches: Promise<unknown>[] = [partialUpdateBrandKit({ id: brandKitId, body: { is_default: true } })];

  if (previousDefaultId && previousDefaultId !== brandKitId) {
    patches.push(partialUpdateBrandKit({ id: previousDefaultId, body: { is_default: false } }));
  }

  Promise.all(patches).catch(() => {
    platformBrandKitStore.update(draft => {
      draft.selectedBrandKitId = previousDefaultId;
      draft.availableBrandKits = draft.availableBrandKits.map(kit => ({
        ...kit,
        is_default: kit.id === previousDefaultId
      }));
    });
  });
}

export function getSelectedPlatformBrandKit(): BrandKit | undefined {
  const { availableBrandKits, selectedBrandKitId } = platformBrandKitStore.getSnapshot();
  const resolvedId = selectedBrandKitId ?? availableBrandKits.find(kit => kit.is_default)?.id;
  return availableBrandKits.find(kit => kit.id === resolvedId) ?? availableBrandKits[0];
}

export function loadBrandKitAndVoiceProfile(): Promise<void> {
  const brandKitPromise = loadBrandKitData();
  const voiceProfilePromise = loadVoiceProfileList();

  return Promise.allSettled([brandKitPromise, voiceProfilePromise]).then(results => {
    const errors = results.filter(result => result.status === 'rejected');
    if (errors.length > 0) {
      return Promise.reject(errors);
    }
  });
}

export function loadClipBrandKit(clipId: string) {
  return loadBrandKitData().then(() => {
    return loadBrandKitData(clipId);
  });
}

export function loadBrandKitData(clipId?: string): Promise<void> {
  const libraryType = clipId ? 'CLIP' : 'ORGANIZATION';
  if (libraryType === 'ORGANIZATION' && brandKit.getSnapshot().id) {
    return Promise.resolve();
  }
  return brandKitList({
    queryParams: {
      library_type: libraryType,
      limit: 1,
      clip_id: clipId
    }
  })
    .then((response: BrandKitListOkResponse) => {
      if (!response.results?.length) {
        createInitialBrandKit(!!clipId).then((res: BrandKitCreateOkResponse) => {
          initBrandKitData(res, clipId);
        });
      } else {
        initBrandKitData(response.results[0], clipId);
      }
    })
    .catch(err => Promise.reject(err));
}

export function loadSharedBrandKitData(clipId: string, fontLocation?: string) {
  if (!fontLocation) {
    return;
  }

  const fontName = decodeURI(getFileNameWithoutExtension(getFileNameFromPath(fontLocation)));

  clipsBrandKit.update(data => {
    const clipBrandKit = data[clipId];
    return {
      ...data,
      [clipId]: {
        ...clipBrandKit,
        fontsMap: {
          brandKit: {
            [fontLocation]: fontName
          }
        }
      }
    };
  });

  const fontFace = new FontFace(fontName, `url(${fontLocation})`);
  fontFace.load().then(loadedFont => {
    document.fonts.add(loadedFont);
  });
}

export function updateIndividualBrandKitConfig(key: string, value: string[], clipId?: string) {
  clipId
    ? clipsBrandKit.update(data => {
        const clipBrandKit = data[clipId];
        return {
          ...data,
          [clipId]: {
            ...clipBrandKit,
            [key]: value
          }
        };
      })
    : brandKit.update(data => ({ ...data, [key]: value }));
}

export function storeBrandKitChanges(clipId?: string): Promise<BrandKitUpdateOkResponse> {
  const currentBrandKit = clipId ? clipsBrandKit.getSnapshot()[clipId] : brandKit.getSnapshot();
  window.analytics?.track('ContentLabEvent', {
    name: 'BrandKitEdited',
    brandKitId: currentBrandKit.id
  });
  // If there is first change to clip brand kit - create it
  if (clipId && !currentBrandKit.id) {
    return createClipBradKit(clipId, currentBrandKit).then(brandKit => {
      return brandKit;
    });
  }
  return brandKitUpdate({
    id: currentBrandKit.id as string,
    body: {
      ...currentBrandKit,
      library_type: clipId ? 'CLIP' : 'ORGANIZATION',
      clip: clipId
    }
  }).then(response => {
    initBrandKitData(response, clipId);
    return response;
  });
}

function createClipBradKit(clipId: string, brandKitState: BrandKitStore) {
  return brandKitCreate({
    body: {
      ...brandKitState,
      library_type: 'CLIP',
      clip: clipId
    }
  }).then(res => {
    clipsBrandKit.update(data => {
      const id = (res as BrandKitCreateResponse).id;
      const clipBrandKit = data[clipId];
      return {
        ...data,
        [clipId]: {
          ...clipBrandKit,
          id
        }
      };
    });
    return res;
  });
}

export function getCurrentClipFontName(clipId: string, fontLocation?: string): string | null {
  return getCurrentClipFontNamePure({
    clip: currentClip.getSnapshot()[clipId],
    clipBrandKitStore: clipsBrandKit.getSnapshot()[clipId],
    fontLocation
  });
}

export function getFontNameFromBrandKit(fontUrl?: string): string {
  return getFontNameFromBrandKitPure({
    fontUrl,
    brandKitStore: brandKit.getSnapshot()
  });
}

function initBrandKitData(res, clipId: string | undefined) {
  const storeValue = {
    colors: res.colors,
    fonts: res.fonts,
    fontsMap: res.fonts.reduce((acc, el) => {
      return {
        ...acc,
        [getBrandKitUrl(el)]: getFileNameWithoutExtension(getFileNameFromPath(el))
      };
    }, {}),
    background: res.background,
    intro_outro: res.intro_outro,
    id: res.id
  };
  if (clipId) {
    clipsBrandKit.update(data => ({ ...data, [clipId]: storeValue }));
    initClipBrandKit(clipId);
  } else {
    brandKit.set(() => storeValue);
  }
}

function initClipBrandKit(clipId: string) {
  const brandKitFontsMap = {
    brandKit: (brandKit.getSnapshot().fonts || []).reduce((acc, el) => {
      return {
        ...acc,
        [getBrandKitUrl(el)]: getFileNameWithoutExtension(getFileNameFromPath(el))
      };
    }, {}),
    predefined: fontsMap()
  };
  loadBrandKitFonts(brandKitFontsMap.brandKit);
  loadBrandKitFonts(brandKitFontsMap.predefined);
  clipsBrandKit.update(data => {
    const clipBrandKit = data[clipId];
    return {
      ...data,
      [clipId]: {
        ...clipBrandKit,
        fontsMap: brandKitFontsMap
      }
    };
  });
}

function createInitialBrandKit(isClip: boolean): Promise<BrandKitCreateOkResponse> {
  return isClip ? createInitialClipBrandKit() : createInitialOrgBrandKit();
}

function createInitialClipBrandKit(): Promise<BrandKitCreateOkResponse> {
  return Promise.resolve({
    ...EMPTY_BRAND_KIT,
    library_type: 'CLIP'
  });
}

function createInitialOrgBrandKit(): Promise<BrandKitCreateOkResponse> {
  return brandKitCreate({
    body: {
      ...EMPTY_BRAND_KIT,
      library_type: 'ORGANIZATION'
    }
  });
}
